import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { sendContactMessage, checkBackendHealth } from '../../services/api';
import './ContactPage.css';

const ContactPage = () => {
  // 1. STATE
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [backendStatus, setBackendStatus] = useState(null); // ✅ NEW

  // 2. EFFECT (Backend Health Check)
  useEffect(() => {
    const checkBackend = async () => {
      try {
        const isHealthy = await checkBackendHealth();
        setBackendStatus(isHealthy);

        if (!isHealthy) {
          setError('Backend server is not responding. Please try again later.');
        }
      } catch (err) {
        setBackendStatus(false);
        setError('Backend server is not responding. Please try again later.');
      }
    };

    checkBackend();
  }, []);

  // 3. HANDLERS
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🚫 Prevent submission if backend is down
    if (backendStatus === false) {
      setError('Cannot send message. Backend server is unavailable.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendContactMessage(formData);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = '918074239533';
    const message = encodeURIComponent(
      `Hi! I'm interested in your services.\n\nName: ${formData.name || 'Not provided'}\nEmail: ${formData.email || 'Not provided'}\nService: ${formData.service || 'Not specified'}\n\nMessage: ${formData.message || 'I would like to know more about your services.'}`
    );
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  // 4. UI
  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <h1>Get In <span className="blue-text">Touch</span></h1>
          <p>Let's discuss how we can help bring your ideas to life</p>
        </div>
      </section>

      <section className="contact-info-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info-cards">
              <div className="info-card">
                <Mail size={24} className="info-icon" />
                <h3>Email Us</h3>
                <p>contact@sritha-solutions.com</p>
              </div>
              <div className="info-card">
                <Phone size={24} className="info-icon" />
                <h3>Call Us</h3>
                <p>+91 8074239533</p>
              </div>
              <div className="info-card">
                <MessageCircle size={24} className="info-icon" />
                <h3>WhatsApp</h3>
                <p>+91 8074239533</p>
                <button onClick={handleWhatsAppClick} className="whatsapp-btn">
                  Message on WhatsApp
                </button>
              </div>
              <div className="info-card">
                <MapPin size={24} className="info-icon" />
                <h3>Visit Us</h3>
                <p>Hyderabad, Telangana, India</p>
              </div>
            </div>

            <div className="contact-form-container">
              {submitted ? (
                <div className="success-message">
                  <h3>Thank You!</h3>
                  <p>We've received your message and will get back to you within 24 hours.</p>
                  <button onClick={() => setSubmitted(false)} className="btn btn-outline">
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <h2>Send Us a Message</h2>

                  {/* 🔴 Backend error display */}
                  {error && <div className="error-message">{error}</div>}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Service Interested In</label>
                    <select name="service" value={formData.service} onChange={handleChange}>
                      <option value="">Select a service</option>
                      <option value="web">Web Development</option>
                      <option value="mobile">Mobile App Development</option>
                      <option value="cloud">Cloud Solutions</option>
                      <option value="ai">AI & Machine Learning</option>
                      <option value="consulting">IT Consulting</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Message *</label>
                    <textarea
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-buttons">
                    <button
                      type="submit"
                      className="btn btn-primary submit-btn"
                      disabled={loading || backendStatus === false} // ✅ disable if backend down
                    >
                      {loading ? 'Sending...' : 'Send Message'} <Send size={18} />
                    </button>

                    <button
                      type="button"
                      onClick={handleWhatsAppClick}
                      className="btn whatsapp-submit-btn"
                    >
                      <MessageCircle size={18} />
                      Send via WhatsApp
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="map-section">
        <div className="container">
          <h2 className="section-title">Our <span className="blue-text">Location</span></h2>
          <div className="map-container">
            <iframe
              title="office-location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d243647.21935664018!2d78.26793336827013!3d17.41260861515927!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb99daeaebd2c7%3A0xae93b78392bafbc2!2sHyderabad%2C%20Telangana!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;